/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { FC, useCallback, useRef, MouseEvent } from "react";
import { FC_ANCHOR } from "../FloatingContainer";
import { Icon, ICON_SIZE } from "../Icon";
import { Tooltip } from "../Tooltip";

export interface IFieldHeaderButtonProps {
	icon: string;
	tooltip?: string;
	disabled?: boolean;
	isActive?: boolean;
	className?: string;
	onClick?: () => void;
}

export const FieldHeaderButton: FC<IFieldHeaderButtonProps> = ({
	icon,
	tooltip,
	disabled,
	isActive,
	className,
	onClick
}) => {
	const buttonElRef = useRef<HTMLButtonElement>();

	const handleClick = useCallback(
		(ev: MouseEvent) => {
			if (onClick) {
				ev.stopPropagation();
				onClick();
			}
		},
		[ onClick ]
	);

	const baseClass = BEM("edt-field-header", "button", {
		"is-active": isActive,
		"is-disabled": disabled
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<button
			className={baseClass.toClassName()}
			disabled={disabled}
			ref={buttonElRef}
			onClick={handleClick}
		>
			<Icon name={icon} size={ICON_SIZE.XSMALL} />
			{tooltip ? (
				<Tooltip for={buttonElRef} anchor={FC_ANCHOR.CENTER_TOP}>
					{tooltip}
				</Tooltip>
			) : null}
		</button>
	);
};
